import { Sun, Moon, Laptop } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useTheme, type Theme } from '@/contexts/ThemeContext'

export type ThemeSwitcherProps = {
  /** sidebar = on red brand strip; surface = cards/login; icon = compact single toggle */
  variant?: 'sidebar' | 'surface' | 'icon'
  showSystem?: boolean
  className?: string
}

export function ThemeSwitcher({
  variant = 'surface',
  showSystem = false,
  className,
}: ThemeSwitcherProps) {
  const { theme, isDark, setTheme } = useTheme()

  const activeMode: 'light' | 'dark' =
    theme === 'light' || theme === 'dark' ? theme : isDark ? 'dark' : 'light'

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={() => setTheme(activeMode === 'dark' ? 'light' : 'dark')}
        className={cn(
          'theme-switcher-icon relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border transition-colors',
          'border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] shadow-sm hover:bg-[var(--muted)]',
          className
        )}
        title={activeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={activeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <span
          key={activeMode}
          className="theme-switcher-icon__spin absolute inset-0 flex items-center justify-center"
        >
          {activeMode === 'dark' ? (
            <Sun className="h-4 w-4 text-amber-400" />
          ) : (
            <Moon className="h-4 w-4 text-[var(--leap-brand)]" />
          )}
        </span>
      </button>
    )
  }

  const options: Array<{ id: Theme; label: string; icon: typeof Sun }> = showSystem
    ? [
        { id: 'light', label: 'Light', icon: Sun },
        { id: 'dark', label: 'Dark', icon: Moon },
        { id: 'system', label: 'System', icon: Laptop },
      ]
    : [
        { id: 'light', label: 'Light', icon: Sun },
        { id: 'dark', label: 'Dark', icon: Moon },
      ]

  const selectedIndex = Math.max(
    0,
    showSystem
      ? options.findIndex((o) => o.id === theme)
      : activeMode === 'dark'
        ? 1
        : 0
  )

  const count = options.length
  const onBrand = variant === 'sidebar'

  return (
    <div
      className={cn(
        'theme-switcher relative grid rounded-lg p-0.5',
        onBrand ? 'bg-black/25' : 'border border-[var(--border)] bg-[var(--muted)]',
        className
      )}
      style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}
      role="group"
      aria-label="Theme"
    >
      <span
        aria-hidden
        className={cn(
          'theme-switcher__thumb pointer-events-none absolute inset-y-0.5 rounded-md shadow-sm',
          onBrand
            ? selectedIndex === 1
              ? 'bg-black'
              : 'bg-white'
            : 'bg-[var(--card)] ring-1 ring-[var(--border)]'
        )}
        style={{
          width: `calc((100% - 4px) / ${count})`,
          transform: `translateX(calc(${selectedIndex} * 100% + 2px))`,
        }}
      />

      {options.map((opt) => {
        const selected = showSystem ? theme === opt.id : activeMode === opt.id
        const Icon = opt.icon
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => setTheme(opt.id)}
            className={cn(
              'relative z-[1] flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors duration-200',
              selected
                ? onBrand
                  ? opt.id === 'dark'
                    ? 'text-white'
                    : 'text-[#af0000]'
                  : 'text-[var(--foreground)]'
                : onBrand
                  ? 'text-white/70 hover:text-white'
                  : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
            )}
            aria-pressed={selected}
            title={opt.id === 'dark' ? 'AMOLED black' : `${opt.label} mode`}
          >
            <Icon
              className={cn(
                'h-3.5 w-3.5 transition-transform duration-300',
                selected && 'theme-switcher__icon-pop'
              )}
            />
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

export default ThemeSwitcher
